import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { adminService } from '@/api/services/admin';
import { changePassword, changeEmail, forgotPassword, signOut, getCurrentUser, getUserDisplayName } from '@/lib/auth';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

// ── Schemas ───────────────────────────────────────────────────────────────────

const addAdminSchema = z.object({
  full_name: z.string().min(2, 'Name must be at least 2 characters').max(100, 'Name must be under 100 characters'),
  email: z.string().email('Invalid email address'),
  password: z.string().min(12, 'Password must be at least 12 characters'),
});
type AddAdminFormData = z.infer<typeof addAdminSchema>;

const disableAdminSchema = z.object({
  email: z.string().email('Invalid email address'),
});
type DisableAdminFormData = z.infer<typeof disableAdminSchema>;

const enableAdminSchema = z.object({
  email: z.string().email('Invalid email address'),
});
type EnableAdminFormData = z.infer<typeof enableAdminSchema>;

const changePasswordSchema = z.object({
  password: z.string().min(12, 'Password must be at least 12 characters'),
  confirm: z.string(),
}).refine((d) => d.password === d.confirm, {
  message: 'Passwords do not match',
  path: ['confirm'],
});
type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

const changeEmailSchema = z.object({
  email: z.string().email('Invalid email address'),
});
type ChangeEmailFormData = z.infer<typeof changeEmailSchema>;

// ── Inline message helper ─────────────────────────────────────────────────────

function InlineSuccess({ message }: { message: string }) {
  return (
    <div className="p-3 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800/30 rounded-md">
      <p className="text-sm text-success-700 dark:text-success-400">{message}</p>
    </div>
  );
}

function InlineError({ message }: { message: string }) {
  return (
    <div className="p-3 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800/30 rounded-md">
      <p className="text-sm text-error-700 dark:text-error-400">{message}</p>
    </div>
  );
}

// ── Section 1: Add Admin ──────────────────────────────────────────────────────

function AddAdminSection() {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const { register, handleSubmit, formState: { errors }, reset } = useForm<AddAdminFormData>({
    resolver: zodResolver(addAdminSchema),
  });

  const onSubmit = async (data: AddAdminFormData) => {
    setSuccess(''); setError(''); setSubmitting(true);
    try {
      await adminService.addNewAdmin(data.full_name, data.email, data.password);
      setSuccess(`Admin ${data.email} created. Share these credentials out-of-band — backend has no email-on-create flow.`);
      reset();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add admin.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add admin</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {success && <InlineSuccess message={success} />}
          {error && <InlineError message={error} />}

          <Input
            {...register('full_name')}
            label="Full name"
            placeholder="Jane Smith"
            error={errors.full_name?.message}
          />
          <Input
            {...register('email')}
            type="email"
            label="Email"
            placeholder="jane@example.com"
            error={errors.email?.message}
          />
          <Input
            {...register('password')}
            type="password"
            label="Password"
            placeholder="At least 12 characters"
            error={errors.password?.message}
          />

          <Button
            type="submit"
            variant="primary"
            disabled={submitting}
          >
            {submitting ? 'Adding...' : 'Add admin'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// ── Section 2: Disable Admin ──────────────────────────────────────────────────

function DisableAdminSection() {
  const [submitting, setSubmitting] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const { register, handleSubmit, formState: { errors }, reset } = useForm<DisableAdminFormData>({
    resolver: zodResolver(disableAdminSchema),
  });

  const onSubmit = (data: DisableAdminFormData) => {
    setSuccess('');
    setError('');
    setPendingEmail(data.email);
  };

  const handleConfirm = async () => {
    if (!pendingEmail) return;
    setSubmitting(true);
    try {
      await adminService.disableAdmin(pendingEmail);
      setSuccess(`Admin ${pendingEmail} has been disabled.`);
      setPendingEmail(null);
      reset();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to disable admin.');
      setPendingEmail(null);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setPendingEmail(null);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Disable admin</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {success && <InlineSuccess message={success} />}
          {error && <InlineError message={error} />}

          <Input
            {...register('email')}
            type="email"
            label="Admin email"
            placeholder="admin@example.com"
            error={errors.email?.message}
          />

          {pendingEmail ? (
            <div className="space-y-3">
              <div className="p-3 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800/30 rounded-md">
                <p className="text-sm text-warning-700 dark:text-warning-400">
                  Disable <strong>{pendingEmail}</strong>? Use the Enable admin section below to reverse this.
                </p>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="secondary" size="sm" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  disabled={submitting}
                  onClick={handleConfirm}
                >
                  {submitting ? 'Disabling...' : 'Confirm disable'}
                </Button>
              </div>
            </div>
          ) : (
            <Button type="submit" variant="danger">
              Disable
            </Button>
          )}
        </form>
      </CardContent>
    </Card>
  );
}

// ── Section 2b: Enable Admin ──────────────────────────────────────────────────

function EnableAdminSection() {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const { register, handleSubmit, formState: { errors }, reset } = useForm<EnableAdminFormData>({
    resolver: zodResolver(enableAdminSchema),
  });

  const onSubmit = async (data: EnableAdminFormData) => {
    setSuccess(''); setError(''); setSubmitting(true);
    try {
      await adminService.enableAdmin(data.email);
      setSuccess(`Admin ${data.email} has been re-enabled.`);
      reset();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to enable admin.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Enable admin</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {success && <InlineSuccess message={success} />}
          {error && <InlineError message={error} />}

          <Input
            {...register('email')}
            type="email"
            label="Admin email"
            placeholder="admin@example.com"
            error={errors.email?.message}
          />

          <Button type="submit" disabled={submitting}>
            {submitting ? 'Enabling...' : 'Enable'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// ── Section 3: My Account ─────────────────────────────────────────────────────

function MyAccountSection() {
  const navigate = useNavigate();
  const user = getCurrentUser();

  const [pwSuccess, setPwSuccess] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  const [emailSuccess, setEmailSuccess] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  const [resetSuccess, setResetSuccess] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const {
    register: registerPw,
    handleSubmit: handlePwSubmit,
    formState: { errors: pwErrors },
    reset: resetPwForm,
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
  });

  const {
    register: registerEmail,
    handleSubmit: handleEmailSubmit,
    formState: { errors: emailErrors },
    reset: resetEmailForm,
  } = useForm<ChangeEmailFormData>({
    resolver: zodResolver(changeEmailSchema),
  });

  const onChangePassword = async (data: ChangePasswordFormData) => {
    setPwLoading(true);
    setPwSuccess('');
    setPwError('');
    const { error } = await changePassword(data.password);
    setPwLoading(false);
    if (error) {
      setPwError(error);
    } else {
      setPwSuccess('Password updated successfully.');
      resetPwForm();
    }
  };

  const onChangeEmail = async (data: ChangeEmailFormData) => {
    setEmailLoading(true);
    setEmailSuccess('');
    setEmailError('');
    const { error } = await changeEmail(data.email);
    setEmailLoading(false);
    if (error) {
      setEmailError(error);
    } else {
      setEmailSuccess('Email updated. You may need to re-login if your session is tied to the old address.');
      resetEmailForm();
    }
  };

  const onResetPassword = async () => {
    const email = user?.email;
    if (!email) return;
    setResetLoading(true);
    setResetSuccess('');
    setResetError('');
    const { error } = await forgotPassword(email);
    setResetLoading(false);
    if (error) {
      setResetError(error);
    } else {
      setResetSuccess('Backend will email a fresh randomly-generated password to your address.');
    }
  };

  const onSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>My account</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Current user info */}
          <div className="pb-4 border-b border-border">
            <p className="text-sm font-medium text-text-primary">{getUserDisplayName(user)}</p>
            <p className="text-xs text-text-secondary">{user?.email}</p>
          </div>

          {/* Change password */}
          <div>
            <h4 className="text-sm font-medium text-text-primary mb-3">Change password</h4>
            <form onSubmit={handlePwSubmit(onChangePassword)} className="space-y-3">
              {pwSuccess && <InlineSuccess message={pwSuccess} />}
              {pwError && <InlineError message={pwError} />}
              <Input
                {...registerPw('password')}
                type="password"
                label="New password"
                placeholder="At least 12 characters"
                error={pwErrors.password?.message}
              />
              <Input
                {...registerPw('confirm')}
                type="password"
                label="Confirm new password"
                placeholder="Repeat new password"
                error={pwErrors.confirm?.message}
              />
              <Button type="submit" variant="secondary" size="sm" disabled={pwLoading}>
                {pwLoading ? 'Saving...' : 'Update password'}
              </Button>
            </form>
          </div>

          {/* Change email */}
          <div className="pt-4 border-t border-border">
            <h4 className="text-sm font-medium text-text-primary mb-3">Change email</h4>
            <form onSubmit={handleEmailSubmit(onChangeEmail)} className="space-y-3">
              {emailSuccess && <InlineSuccess message={emailSuccess} />}
              {emailError && <InlineError message={emailError} />}
              <Input
                {...registerEmail('email')}
                type="email"
                label="New email"
                placeholder="new@example.com"
                error={emailErrors.email?.message}
              />
              <Button type="submit" variant="secondary" size="sm" disabled={emailLoading}>
                {emailLoading ? 'Saving...' : 'Update email'}
              </Button>
            </form>
          </div>

          {/* Reset password by email */}
          <div className="pt-4 border-t border-border">
            <h4 className="text-sm font-medium text-text-primary mb-1">Reset password by email</h4>
            <p className="text-xs text-text-secondary mb-3">
              Backend will email a fresh randomly-generated password to your address.
            </p>
            {resetSuccess && <div className="mb-3"><InlineSuccess message={resetSuccess} /></div>}
            {resetError && <div className="mb-3"><InlineError message={resetError} /></div>}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={resetLoading}
              onClick={onResetPassword}
            >
              {resetLoading ? 'Sending...' : 'Send reset email'}
            </Button>
          </div>

          {/* Sign out */}
          <div className="pt-4 border-t border-border">
            <Button type="button" variant="danger" size="sm" onClick={onSignOut}>
              Sign out
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function Admins() {
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-text-primary">Admins</h1>
      <AddAdminSection />
      <DisableAdminSection />
      <EnableAdminSection />
      <MyAccountSection />
    </div>
  );
}
